/**
 * Apply a resolved EmbedTheme to the iframe document and to the parent
 * launcher (via postMessage). Called once /embedded/resolve returns.
 */
import { resolveEmbedTheme, themeToCssVars, type EmbedTheme } from '@wg/theme';

export function applyThemeToDocument(stored: unknown): EmbedTheme {
  const theme = resolveEmbedTheme(stored);
  if (typeof document === 'undefined') return theme;
  const vars = themeToCssVars(theme);
  for (const [k, v] of Object.entries(vars)) {
    document.documentElement.style.setProperty(k, v);
  }
  // Legacy renderer CSS (CardRenderer, ChipRenderer, FormRenderer, etc.)
  // references `--wg-primary`, `--wg-text`, `--wg-surface`, etc. — names
  // from the original PartnerTheme namespace before the embed theme was
  // unified under `--wg-color-*`. Until those CSS files are migrated,
  // mirror the canonical tokens onto the legacy names so partner themes
  // actually flow through to cards/chips/forms. `--wg-surface-muted` and
  // `--wg-fg-faint` don't have direct equivalents — derive them off the
  // canonical text color with low alpha so they read as faint chrome
  // regardless of light/dark partner palettes.
  // `--wg-surface-muted` is consumed as a BUTTON BACKGROUND
  // (CardRenderer secondary action, FormRenderer chip surfaces, etc).
  // The previous mapping pointed it at theme.colors.border, which is a
  // thin line color — buttons rendered as near-invisible smudges on
  // dark themes. Use a CSS color-mix tint of surface so the muted
  // background reads as "surface, slightly differentiated" on every
  // partner palette. Unrelated `--wg-line` / `--wg-card-border` keep
  // mapping to border because they ARE line tokens.
  const surfaceMuted = `color-mix(in srgb, ${theme.colors.surface} 88%, ${theme.colors.text} 12%)`;
  const legacy: Record<string, string> = {
    '--wg-primary': theme.colors.primary,
    '--wg-primary-text': theme.colors.primaryContrast,
    '--wg-text': theme.colors.text,
    '--wg-text-muted': theme.colors.textMuted,
    '--wg-surface': theme.colors.surface,
    '--wg-surface-muted': surfaceMuted,
    '--wg-line': theme.colors.border,
    '--wg-card-bg': theme.colors.surface,
    '--wg-card-border': theme.colors.border,
    '--wg-fg': theme.colors.text,
    '--wg-fg-faint': theme.colors.textMuted,
    '--wg-destructive': '#d94c4c',
  };
  for (const [k, v] of Object.entries(legacy)) {
    document.documentElement.style.setProperty(k, v);
  }
  document.documentElement.dataset.wgPreset = theme.preset;
  document.documentElement.dataset.wgMode = theme.mode;
  return theme;
}

export function postLauncherToParent(
  theme: EmbedTheme,
  targetOrigin: string,
): void {
  if (typeof window === 'undefined' || window.parent === window) return;
  try {
    window.parent.postMessage(
      {
        type: 'wg:theme_apply',
        payload: {
          launcher: theme.launcher,
          panel: theme.panel,
          colors: theme.colors,
          mode: theme.mode,
          // headerLayout drives whether the bootstrap bumps the iframe
          // height by 32px to make room for the avatar overhang above
          // the visible chat panel. Without this hint the bootstrap
          // would steal 32px from chat content instead of adding it.
          headerLayout: theme.headerLayout,
        },
      },
      targetOrigin,
    );
  } catch {
    /* noop — parent may be different origin and that's fine */
  }
}

/**
 * Push up to 3 suggestion strings to the parent bootstrap so it can render
 * them as tappable pills next to the collapsed launcher. Send `[]` to clear
 * (e.g. instance config was edited and chips are no longer wanted). The
 * parent caps to 3 itself, but we also cap here to avoid sending bytes
 * that would be discarded.
 */
export function postLauncherSuggestionsToParent(
  items: string[],
  targetOrigin: string,
): void {
  if (typeof window === 'undefined' || window.parent === window) return;
  try {
    window.parent.postMessage(
      {
        type: 'wg:launcher_suggestions',
        payload: {
          items: (items || [])
            .filter(s => typeof s === 'string' && s.trim())
            .slice(0, 3),
        },
      },
      targetOrigin,
    );
  } catch {
    /* noop */
  }
}

export { resolveEmbedTheme };
export type { EmbedTheme };
